import React from 'react';
import PropTypes from 'prop-types';
import '../../polyfills/polyfill-allSettled';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import ContainerWrapper from '../styled components/ContainerWrapper';
import Sticky from '../styled components/Sticky';
import EventsActionbar from '../events-list/EventsActionbar/EventsActionbar';
import RequestHandler from '../libs/requestHandling';
import {
  getWeekOf,
  getWeekDaysBetween,
  isOnSameDate,
  getWeekNumber,
  serializeYesplanAPIDate,
  deserializeYesplanAPIDate
} from '../libs/dateTimeUtilities';
import EventsWeek from '../events-list/EventsWeek/EventsWeek';
import SessionStorageContext from '../components/SessionStorageContext';
import { formatEventOrEventGroup } from '../libs/ypAPIDataFormatter';
import {
  convertFilterToYPQuery,
  getDefaultFilter,
  postQueryResultFiltering
} from '../libs/eventsFilters';
import CacheResultsContext from '../components/CacheResultsContext';
import {
  fixJumpingStickyHeaderCollapsible,
  maintainScrollPositionCollapsible,
  getScrollPositionToGetCollapsibleContentIntoView,
  getDefaultAppScrollContainer,
  setScrollPositionAppContainer
} from '../libs/fixDefaultScrollposition';
import DateNavigationUrlWrapper from '../components/DateNavigationUrlWrapper';
import debounce from '../libs/debounce';
import { cacheResultForEventsKey } from '../libs/cacheKeys';
import { sessionStorageItemNameEventsListPagination } from '../libs/sessionStorageItemNames';
import EventlistError from '../components/Errors/EventlistError';
import YPEventsAndGroupsStructure from '../yesplan_classes/YPEventsAndGroupsStructureObject';
import NavigationHistory from '../libs/NavigationHistory';
import {
  capture,
  PH_EVENT_DAY_OPENED,
  PH_EVENTS_FILTER_APPLIED,
  PH_EVENTS_FILTER_OPENED
} from '../libs/posthogAnalytics';


class EventsContainer extends React.Component {
  static defaultNumberOfEventsToShow = 30;

  static widgetName = 'events';

  constructor(props) {
    super(props);
    const initiallyShowFilters = !!(
      props.location.state && props.location.state.keepFilterDrawerOpen
    );
    if (initiallyShowFilters) this.eventsFilterListUseMotion = false;

    props.replaceUrlWithWeekAndDateUrl(EventsContainer.widgetName, true);
    const { dateObject, week } = props.getDateAndWeekFromUrlOrFromCurrentDate(
      EventsContainer.widgetName,
      true
    );
    this.state = {
      events: props.getCacheResultValue(cacheResultForEventsKey) || {},
      eventsStructure: new YPEventsAndGroupsStructure([]),
      currentWeek: week,
      dateObjects: getWeekDaysBetween(week.startDateObject, week.endDateObject),
      headerHeight: 0,
      dateRangeObjectArray: this.defineDateRangeArray(week),
      showDateRangePicker: false,
      showFilters: initiallyShowFilters,
      stickyPosition: 0,
      lastClosedDateEl: null,
      actionbarDropdownHeight: this.calculateActionbarDropdownHeight(),
      isFetchingMoreEvents: false,
      numberOfShownEventsPerDate: {},
      fetchMoreError: false,
      openDateObjects: [dateObject],
      errorCode: false
    };

    [
      'renderContent',
      'openFilterEditor',
      'goToNextWeek',
      'goToPreviousWeek',
      'fetchEventsForCurrentWeek',
      'fetchEventsForOpenDates',
      'onWeekNameClick',
      'defineDateRangeArray',
      'onChangeDateRange',
      'setUserPreferenceEventFilters',
      'setFilterActive',
      'onDateTitleClick',
      'setStickyPosition',
      'scrollEventsIntoView',
      'showMoreEventsOnDate',
      'hasDateMoreEventsToShow',
      'numberOfNextEventsToShow',
      'calculateStickyPosition',
      'adjustedOffsetToScrollFirstEventIntoView',
      'calculateActionbarDropdownHeight'
    ].forEach((eventHandler) => {
      this[eventHandler] = this[eventHandler].bind(this);
    });

    this.toggleShowFilters = debounce(this.toggleShowFilters.bind(this));

    this.containerWrapperRef = React.createRef();
    this.eventsWeekRef = React.createRef();
    this.eventsActionbarRef = React.createRef();
    this.filtersDropdownRef = React.createRef();
    this.datepickerDropdownRef = React.createRef();
    this.eventsFetchGeneration = 0;
  }

  componentDidMount() {
    const { headerRef, eventsFilterOptions, fetchEventFilterOptions } = this.props;
    if (eventsFilterOptions === null) fetchEventFilterOptions();
    else {
      this.fetchEventsForOpenDates();
    }
    this.setStickyPosition();
    if (headerRef) {
      if (headerRef.current) {
        this.setState({
          headerHeight: headerRef.current.getHeaderHeight() + 1
        });
      }
    }
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    const { lastClosedDateEl } = this.state;

    const previousStateLastClosedDateEl = lastClosedDateEl;
    const previousScrollContainerRef = prevProps.scrollContainerRef;
    const updatedScrollContainerRef = this.props.scrollContainerRef;

    if (lastClosedDateEl) {
      if (previousScrollContainerRef && updatedScrollContainerRef) {
        if (previousScrollContainerRef.current && updatedScrollContainerRef.current) {
          const { stickyPosition } = this.state;
          return fixJumpingStickyHeaderCollapsible(
            previousStateLastClosedDateEl,
            lastClosedDateEl,
            previousScrollContainerRef.current,
            stickyPosition,
            1
          );
        }
      }
    }
    // If no scroll position correction is needed, return null
    return null;
  }

  componentDidUpdate(prevProps, prevState, scrollFixes) {
    const { user, eventsFilterOptions } = this.props;
    if (this.calculateActionbarDropdownHeight() !== this.state.actionbarDropdownHeight) {
      this.setActionbarDropdownHeight();
    }
    if (this.calculateStickyPosition() !== this.state.stickyPosition) {
      this.setStickyPosition();
    }
    if (
      !isEqual(
        getDefaultFilter(prevProps.user.preferences.eventsFiltersApp),
        getDefaultFilter(user.preferences.eventsFiltersApp)
      )
    ) {
      this.eventsFetchGeneration += 1;
      this.fetchEventsForCurrentWeek(this.eventsFetchGeneration);
    }
    if (
      !isEqual(
        prevState.numberOfShownEventsPerDate,
        this.state.numberOfShownEventsPerDate
      )
    )
      this.setDateToNumberOfShownEventsMappingInSessionStorage();
    if (prevProps.eventsFilterOptions === null && eventsFilterOptions !== null)
      this.fetchEventsForOpenDates();

    if (scrollFixes !== null) {
      const { stickyPosition } = this.state;
      const { scrollContainerRef } = this.props;

      if (scrollContainerRef) {
        if (scrollContainerRef.current) {
          const correctedScrollPos = maintainScrollPositionCollapsible(
            scrollFixes,
            stickyPosition
          );
          if (correctedScrollPos) {
            setScrollPositionAppContainer(
              correctedScrollPos,
              scrollContainerRef.current,
              false
            );
          }
        }
      }
    }
  }

  componentWillUnmount() {
    const {
      navigationHistory,
      setEventsListPagination,
      resetCacheResultValue,
      setCacheResultValue
    } = this.props;
    if (
      navigationHistory.isNavigatingBetweenWidgets() &&
      !navigationHistory.isNavigatingInSameWidget()
    ) {
      setCacheResultValue(cacheResultForEventsKey, this.state.events);
    } else {
      setEventsListPagination({});
      resetCacheResultValue(cacheResultForEventsKey);
    }
  }

  onDateTitleClick(isCollapsed, dateObj, collapsibleDayEl) {
    const { openDateObjects, currentWeek } = this.state;
    const { goToUrlForWeekAndDate } = this.props;
    if (!isCollapsed) {
      capture(PH_EVENT_DAY_OPENED, {
        date: serializeYesplanAPIDate(dateObj)
      });
      this.setState(
        {
          openDateObjects: [...openDateObjects, dateObj],
          lastClosedDateEl: null
        },
        () => {
          goToUrlForWeekAndDate(
            EventsContainer.widgetName,
            currentWeek,
            dateObj,
            true,
            false
          );
        }
      );

      this.fetchEvents(dateObj, true);
    } else {
      this.setState(
        {
          openDateObjects: openDateObjects.filter((d) => !isOnSameDate(d, dateObj)),
          lastClosedDateEl: collapsibleDayEl
        },
        () => {
          goToUrlForWeekAndDate(
            EventsContainer.widgetName,
            currentWeek,
            null,
            true,
            false
          );
        }
      );
    }
  }

  /* Range is an array of date objects passed on by DateRangePicker */
  onChangeDateRange(range) {
    const { goToUrlForWeekAndDate } = this.props;
    const startDateObject = deserializeYesplanAPIDate(range[0].startDate); // Sends JS Dates
    const weekOfRange = getWeekOf(startDateObject);
    const updatedDateRangeObjectArray = this.defineDateRangeArray(weekOfRange);
    const newDates = getWeekDaysBetween(
      weekOfRange.startDateObject,
      weekOfRange.endDateObject
    );
    const { scrollContainerRef } = this.props;
    if (getWeekNumber(weekOfRange) === getWeekNumber(this.state.currentWeek)) {
      this.setState(
        {
          dateRangeObjectArray: updatedDateRangeObjectArray,
          currentWeek: weekOfRange,
          dateObjects: newDates,
          showDateRangePicker: false,
          openDateObjects: [startDateObject],
          lastClosedDateEl: null
        },
        () => {
          this.setStickyPosition();
          setScrollPositionAppContainer(0, scrollContainerRef.current);
          this.fetchEventsForOpenDates();
          goToUrlForWeekAndDate(
            EventsContainer.widgetName,
            weekOfRange,
            startDateObject,
            false
          );
        }
      );
    } else {
      setScrollPositionAppContainer(0, scrollContainerRef.current);
      goToUrlForWeekAndDate(
        EventsContainer.widgetName,
        weekOfRange,
        startDateObject,
        true
      );
    }
  }

  onWeekNameClick() {
    const { showDateRangePicker } = this.state;
    this.setState({
      showDateRangePicker: !showDateRangePicker,
      showFilters: false
    });
  }

  setUserPreferenceEventFilters(filters, callback = false, sendRequest = true) {
    const { setUserPreferenceEventFilters } = this.props;
    setUserPreferenceEventFilters(filters, callback, sendRequest);
    this.closeFiltersAndDatePicker();
    capture(PH_EVENTS_FILTER_APPLIED, {
      filterCount: filters ? Object.keys(filters).length : 0,
    });
  }

  setStickyPosition() {
    this.setState({
      stickyPosition: this.calculateStickyPosition()
    });
  }

  setFilterActive(filter) {
    this.setState({
      activeFilter: filter,
      showFilters: false
    });
  }

  setActionbarDropdownHeight() {
    this.setState({
      actionbarDropdownHeight: this.calculateActionbarDropdownHeight()
    });
  }

  setDateToNumberOfShownEventsMappingInSessionStorage() {
    this.props.setEventsListPagination(this.state.numberOfShownEventsPerDate);
  }

  calculateStickyPosition() {
    const { headerRef } = this.props;
    const { eventsActionbarRef } = this;
    if (headerRef) {
      if (headerRef.current) {
        if (eventsActionbarRef) {
          if (eventsActionbarRef.current) {
            return (
              headerRef.current.getHeaderHeight() +
              eventsActionbarRef.current.offsetHeight +
              1
            );
          }
        }
      }
    }
    return 0;
  }

  calculateActionbarDropdownHeight() {
    if (this.filtersDropdownRef || this.datepickerDropdownRef) {
      if (this.filtersDropdownRef.current || this.datepickerDropdownRef.current) {
        if (this.filtersDropdownRef.current) {
          return this.filtersDropdownRef.current.clientHeight;
        }
        if (this.datepickerDropdownRef.current) {
          return this.datepickerDropdownRef.current.clientHeight;
        }
      }
    }
    return 0;
  }

  hasDateMoreEventsToShow(dateObj) {
    const { numberOfShownEventsPerDate, events } = this.state;
    const dateString = serializeYesplanAPIDate(dateObj);
    const numberOfShownEventsPerDateElement = numberOfShownEventsPerDate[dateString];
    return (
      numberOfShownEventsPerDateElement &&
      numberOfShownEventsPerDateElement < events[dateString].getNumberOfEvents()
    );
  }

  numberOfNextEventsToShow(dateObj) {
    const { numberOfShownEventsPerDate, events } = this.state;
    const dateString = serializeYesplanAPIDate(dateObj);
    const numberOfShownEventsPerDateElement = numberOfShownEventsPerDate[dateString];
    if (
      numberOfShownEventsPerDateElement + EventsContainer.defaultNumberOfEventsToShow <=
      events[dateString].getNumberOfEvents()
    )
      return EventsContainer.defaultNumberOfEventsToShow;
    return events[dateString].getNumberOfEvents() - numberOfShownEventsPerDateElement;
  }

  goToPreviousWeek() {
    const { currentWeek } = this.state;
    const { scrollContainerRef } = this.props;
    setScrollPositionAppContainer(0, scrollContainerRef.current);
    this.props.goToUrlForPreviousWeek(EventsContainer.widgetName, currentWeek);
  }

  goToNextWeek() {
    const { currentWeek } = this.state;
    const { scrollContainerRef } = this.props;
    setScrollPositionAppContainer(0, scrollContainerRef.current);
    this.props.goToUrlForNextWeek(EventsContainer.widgetName, currentWeek);
  }

  toggleShowFilters() {
    const { showFilters } = this.state;
    this.setState(
      {
        showFilters: !showFilters,
        showDateRangePicker: false
      },
      () => {}
    );
  }

  openFilterEditor(filterIndex, editExistingFilter) {
    const { linkUrlFor, navigationHistory } = this.props;
    setScrollPositionAppContainer(0);
    capture(PH_EVENTS_FILTER_OPENED, {
      filterIndex: filterIndex,
      editExisting: !!editExistingFilter,
    });
    navigationHistory.goTo(linkUrlFor(`/events/filters`), false, {
      state: { filterIndex, editExistingFilter }
    });
  }

  defineDateRangeArray(weekOfRange) {
    const firstDayOfWeek = weekOfRange.startDateObject;
    const lastDayOfWeek = weekOfRange.endDateObject;
    return [
      {
        startDate: firstDayOfWeek,
        endDate: lastDayOfWeek,
        key: 'selection'
      }
    ];
  }

  closeFiltersAndDatePicker() {
    this.setState(
      {
        showFilters: false,
        showDateRangePicker: false
      },
      () => {}
    );
  }

  calculateEventlistStickyTop() {
    if (this.containerWrapperRef && this.containerWrapperRef.current) {
      return this.containerWrapperRef.current.clientHeight + 1;
    }
    return 0;
  }

  adjustedOffsetToScrollFirstEventIntoView(collapsibleEl) {
    const collapsibleContentWrapper = collapsibleEl.querySelector(
      '[data-js-wrapper-listitem-collapsible-content]'
    );
    let scrollIntoViewOffset = 84;
    if (collapsibleContentWrapper) {
      // eslint-disable-next-line no-undef
      const pTopCollapsibleContentWrapper = getComputedStyle(
        collapsibleContentWrapper
      ).paddingTop;
      const allowedOffset = pTopCollapsibleContentWrapper
        ? parseInt(pTopCollapsibleContentWrapper.split('px')[0])
        : 0;

      /* The first child of the collapsible content of a day could be one of these options:
    - A message saying there are no events for this day
    - An event group header (can be followed by a subgroup header or an EventInfoCard)
    - An EventInfoCard for an ungrouped event

    When the first EventInfoCard is grouped,
    we still want to scroll part of the EventInfoCard into view.

 */
      const firstEventInfoCard = collapsibleEl.querySelector('[data-js-eventinfo-card]');
      if (firstEventInfoCard) {
        if (firstEventInfoCard.offsetTop !== allowedOffset) {
          /* The EventInfoCard is placed further beneath the start of the collapsible content (caused by (sub)groupheaders) */
          /* We calculate this into the offset to use to scroll the EventInfoCard into view */
          const neededAdditionalOffset = firstEventInfoCard.offsetTop - allowedOffset;
          scrollIntoViewOffset += neededAdditionalOffset;
        }
      }
    }
    return scrollIntoViewOffset;
  }

  scrollEventsIntoView(dateObj) {
    const collapsibleEl = document.querySelector(
      `[data-js-collapsible-listitem-fordate="${serializeYesplanAPIDate(dateObj)}"]`
    );
    const { scrollContainerRef } = this.props;
    if (scrollContainerRef) {
      const currentScrollContainer =
        scrollContainerRef.current || getDefaultAppScrollContainer();
      if (currentScrollContainer) {
        const scrollIntoViewOffset =
          this.adjustedOffsetToScrollFirstEventIntoView(collapsibleEl);

        const newScrollTopPos = getScrollPositionToGetCollapsibleContentIntoView(
          collapsibleEl,
          scrollIntoViewOffset,
          currentScrollContainer
        );
        if (newScrollTopPos) {
          setScrollPositionAppContainer(newScrollTopPos, currentScrollContainer, true);
        }
      }
    }
  }

  showMoreEventsOnDate(dateObj) {
    const dateString = serializeYesplanAPIDate(dateObj);
    this.setState((prevState) => {
      const { numberOfShownEventsPerDate, events } = prevState;
      const numberOfShownEventsPerDateElement = numberOfShownEventsPerDate[dateString];
      return {
        numberOfShownEventsPerDate: {
          ...numberOfShownEventsPerDate,
          [dateString]: Math.min(
            events[dateString].getNumberOfEvents(),
            numberOfShownEventsPerDateElement +
              EventsContainer.defaultNumberOfEventsToShow
          )
        }
      };
    });
  }

  /* fetchEvents keeps the number of already shown events per date, so that a refetch of a date
     does not rewind the "Show Next" pagination while the user is paging through it. Only called
     when the filter changed, which does give a different list of events, so reset it here. */
  fetchEventsForCurrentWeek(requestGeneration = this.eventsFetchGeneration) {
    const { dateObjects } = this.state;
    this.setState({ numberOfShownEventsPerDate: {} });
    dateObjects.forEach((dateObj) =>
      this.fetchEvents(dateObj, false, null, requestGeneration)
    );
  }

  fetchEventsForOpenDates() {
    const { openDateObjects } = this.state;
    const showCorrectNumberOfEventsPerDate = (dateObj) => {
      const { eventsListPagination } = this.props;
      if (eventsListPagination) {
        const previouslyShownEventsOnDate =
          eventsListPagination[serializeYesplanAPIDate(dateObj)];
        if (
          previouslyShownEventsOnDate &&
          previouslyShownEventsOnDate > EventsContainer.defaultNumberOfEventsToShow
        ) {
          this.setState((prevState) => ({
            numberOfShownEventsPerDate: {
              ...prevState.numberOfShownEventsPerDate,
              [serializeYesplanAPIDate(dateObj)]: previouslyShownEventsOnDate
            }
          }));
        }
      }
    };
    const cachedResults = this.props.getCacheResultValue(cacheResultForEventsKey);
    openDateObjects.forEach((dateObj) => {
      if (!cachedResults || !cachedResults[serializeYesplanAPIDate(dateObj)])
        this.fetchEvents(dateObj, false, () => showCorrectNumberOfEventsPerDate(dateObj));
      else showCorrectNumberOfEventsPerDate(dateObj);
    });
  }

  /* The scrollIntoView parameter should only be set to true when you're fetching events of one dateObject at a time (for example when opening a single day).
    If this would be set to true when for example going to all the days of the week, the scrolling would sequentially
    happen for each day, which is not desirable.
  */
  fetchEvents(
    dateObj,
    scrollIntoView,
    whenDoneCallback = null,
    requestGeneration = this.eventsFetchGeneration
  ) {
    const { user, eventsFilterOptions } = this.props;
    const defaultFilter = getDefaultFilter(user.preferences.eventsFiltersApp);
    new RequestHandler().getWithQueryParameters(
      '/events',
      {
        yesplanurl: user.host
      },
      { ypquery: convertFilterToYPQuery(dateObj, defaultFilter, eventsFilterOptions) },
      (response, error) => {
        if (requestGeneration !== this.eventsFetchGeneration) return;
        if (error) {
          this.setState({
            errorCode: error.code
          });
          return;
        }
        const hasMoreResults = RequestHandler.hasPagination(response);
        if (hasMoreResults) {
          this.fetchEventsViaPaginationURL(
            dateObj,
            response.body.pagination.next,
            (nextError, nextEvents) => {
              if (requestGeneration !== this.eventsFetchGeneration) return;
              if (nextError) {
                this.setState(
                  {
                    fetchMoreError: true,
                    isFetchingMoreEvents: false
                  },
                  () => {
                    if (whenDoneCallback) whenDoneCallback();
                    if (scrollIntoView) this.scrollEventsIntoView(dateObj);
                  }
                );
              } else {
                this.setState(
                  (prevState) => ({
                    isFetchingMoreEvents: false,
                    events: {
                      ...prevState.events,
                      [serializeYesplanAPIDate(dateObj)]: new YPEventsAndGroupsStructure(
                        postQueryResultFiltering(
                          [...response.body.data, ...nextEvents],
                          dateObj,
                          defaultFilter,
                          eventsFilterOptions
                        ).map(formatEventOrEventGroup)
                      )
                    },
                    numberOfShownEventsPerDate: {
                      ...prevState.numberOfShownEventsPerDate,
                      [serializeYesplanAPIDate(dateObj)]:
                        prevState.numberOfShownEventsPerDate[
                          serializeYesplanAPIDate(dateObj)
                        ] ?? EventsContainer.defaultNumberOfEventsToShow
                    }
                  }),
                  () => {
                    if (whenDoneCallback) whenDoneCallback();
                    if (scrollIntoView) this.scrollEventsIntoView(dateObj);
                  }
                );
              }
            },
            requestGeneration
          );
        } else {
          this.setState(
            (prevState) => ({
              events: {
                ...prevState.events,
                [serializeYesplanAPIDate(dateObj)]: new YPEventsAndGroupsStructure(
                  postQueryResultFiltering(
                    response.body.data,
                    dateObj,
                    defaultFilter,
                    eventsFilterOptions
                  ).map(formatEventOrEventGroup)
                )
              },
              numberOfShownEventsPerDate: {
                ...prevState.numberOfShownEventsPerDate,
                [serializeYesplanAPIDate(dateObj)]:
                  prevState.numberOfShownEventsPerDate[
                    serializeYesplanAPIDate(dateObj)
                  ] ??
                  Math.min(
                    EventsContainer.defaultNumberOfEventsToShow,
                    response.body.data.length
                  )
              }
            }),
            () => {
              if (whenDoneCallback) whenDoneCallback();
              if (scrollIntoView) this.scrollEventsIntoView(dateObj);
            }
          );
        }
      }
    );
  }

  fetchEventsViaPaginationURL(
    dateObj,
    paginationURL,
    resultsCallback,
    requestGeneration = this.eventsFetchGeneration
  ) {
    if (requestGeneration !== this.eventsFetchGeneration) return;
    this.setState({
      isFetchingMoreEvents: true
    });
    const { user, eventsFilterOptions } = this.props;
    const defaultFilter = getDefaultFilter(user.preferences.eventsFiltersApp);
    new RequestHandler().getFromPaginationURL(
      '/events',
      {
        yesplanurl: user.host
      },
      {
        ypquery: convertFilterToYPQuery(dateObj, defaultFilter, eventsFilterOptions)
      },
      paginationURL,
      (response, error) => {
        if (requestGeneration !== this.eventsFetchGeneration) return;
        if (error) {
          resultsCallback(error, null);
          return;
        }
        const hasMoreResults = RequestHandler.hasPagination(response);
        const events = response.body.data;
        if (hasMoreResults) {
          this.fetchEventsViaPaginationURL(
            dateObj,
            response.body.pagination.next,
            (nextError, nextEvents) =>
              resultsCallback(
                error,
                nextEvents !== null ? [...events, ...nextEvents] : events
              ),
            requestGeneration
          );
        } else {
          resultsCallback(null, events);
        }
      }
    );
  }

  renderErrorMessage() {
    const { widgetPageHeaderRef, navigationHistory } = this.props;
    const { errorCode } = this.state;

    return (
      <ContainerWrapper
        $paddingTop={
          widgetPageHeaderRef.current ? widgetPageHeaderRef.current.getHeaderHeight() : 0
        }
        data-test-type="errorMessage"
      >
        <EventlistError errorCode={errorCode} navigationHistory={navigationHistory} />
      </ContainerWrapper>
    );
  }

  renderContent() {
    const { user, headerRef, navigationHistory, linkUrlFor } = this.props;
    const { eventsFiltersApp } = user.preferences;
    const { dateObjects, events, headerHeight, isFetchingMoreEvents, errorCode } =
      this.state;
    if (errorCode) return this.renderErrorMessage();
    return (
      <div>
        <Sticky
          ref={this.containerWrapperRef}
          $zIndex={5}
          tw="bg-silver-lighten-30"
          data-test-events-header
        >
          <ContainerWrapper
            tw="px-0"
            $paddingTop={headerHeight}
            $relative
          >
            <EventsActionbar
              ref={this.eventsActionbarRef}
              datepickerDropdownRef={this.datepickerDropdownRef}
              filtersDropdownRef={this.filtersDropdownRef}
              actionbarDropdownHeight={this.state.actionbarDropdownHeight}
              user={user}
              filters={eventsFiltersApp}
              setUserPreferenceEventFilters={this.setUserPreferenceEventFilters}
              openFilterEditor={this.openFilterEditor}
              goToPreviousWeek={this.goToPreviousWeek}
              goToNextWeek={this.goToNextWeek}
              onChangeDateRange={this.onChangeDateRange}
              toggleShowFilters={this.toggleShowFilters}
              onWeekNameClick={this.onWeekNameClick}
              linkUrlFor={linkUrlFor}
              navigationHistory={navigationHistory}
              eventsWeekRef={this.eventsWeekRef}
              $paddingTop={headerHeight}
              eventsFilterListUseMotion={this.eventsFilterListUseMotion}
              {...omit(this.state, ['events', 'dateObjects'])}
            />
          </ContainerWrapper>
        </Sticky>

        <EventsWeek
          ref={this.eventsWeekRef}
          containerWrapperRef={this.containerWrapperRef}
          eventsActionbarRef={this.eventsActionbarRef}
          headerRef={headerRef}
          events={events}
          dates={dateObjects}
          stickyPosition={this.state.stickyPosition}
          openDates={this.state.openDateObjects}
          onDateTitleClick={this.onDateTitleClick}
          showMoreEventsOnDate={this.showMoreEventsOnDate}
          isFetchingMoreEvents={isFetchingMoreEvents}
          hasDateMoreEventsToShow={this.hasDateMoreEventsToShow}
          eventsFiltersApp={user.preferences.eventsFiltersApp}
          numberOfShownEventsPerDate={this.state.numberOfShownEventsPerDate}
          numberOfNextEventsToShow={this.numberOfNextEventsToShow}
          hasFetchMoreError={this.state.fetchMoreError}
          {...this.props}
        />
      </div>
    );
  }

  render() {
    return this.renderContent();
  }
}

EventsContainer.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    preferences: PropTypes.shape({
      eventsFiltersApp: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  eventsFilterListUseMotion: PropTypes.bool,
  headerRef: PropTypes.shape({
    current: PropTypes.shape({
      getHeaderHeight: PropTypes.func
    })
  }),
  /* entire scrollContainer of the widget */
  scrollContainerRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollTop: PropTypes.number
    })
  }).isRequired,
  eventsFilterOptions: PropTypes.shape({}),
  eventsListPagination: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      keepFilterDrawerOpen: PropTypes.bool
    })
  }).isRequired,
  setEventsListPagination: PropTypes.func.isRequired,
  fetchEventFilterOptions: PropTypes.func.isRequired,
  setUserPreferenceEventFilters: PropTypes.func.isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  replaceUrlWithWeekAndDateUrl: PropTypes.func.isRequired,
  getDateAndWeekFromUrlOrFromCurrentDate: PropTypes.func.isRequired,
  goToUrlForWeekAndDate: PropTypes.func.isRequired,
  goToUrlForPreviousWeek: PropTypes.func.isRequired,
  goToUrlForNextWeek: PropTypes.func.isRequired,
  resetCacheResultValue: PropTypes.func.isRequired,
  getCacheResultValue: PropTypes.func.isRequired,
  setCacheResultValue: PropTypes.func.isRequired
};

EventsContainer.defaultProps = {
  eventsFilterListUseMotion: true,
  eventsFilterOptions: null,
  headerRef: null
};

function ForwardRef(props, ref) {
  return (
    <SessionStorageContext.Consumer>
      {({ getSessionStorageValue, setSessionStorageValue }) => (
        <CacheResultsContext.Consumer>
          {(cacheResultsContextProps) => (
            <DateNavigationUrlWrapper
              {...props}
              {...cacheResultsContextProps}
              headerRef={ref}
              eventsListPagination={getSessionStorageValue(
                sessionStorageItemNameEventsListPagination
              )}
              setEventsListPagination={(dateToPaginationNumberObject) => {
                setSessionStorageValue(
                  sessionStorageItemNameEventsListPagination,
                  dateToPaginationNumberObject
                );
              }}
            >
              {(propsFromWrapper) => <EventsContainer {...propsFromWrapper} />}
            </DateNavigationUrlWrapper>
          )}
        </CacheResultsContext.Consumer>
      )}
    </SessionStorageContext.Consumer>
  );
}

export default React.forwardRef(ForwardRef);
